/**
 * TOUR-03 lot 9 (U-TOUR-03-1, amendée) : atelier en onglets et volet masquable. Logique pure, sans React.
 * L'état de l'atelier concerné est `{voletReplie: boolean, onglet: 'carte' | 'journees' | 'sensibilite'}`.
 */

/* ------------------------------------------------------------------ L9-1 : volet replié mémorisé */

export const CLE_VOLET_REPLIE = 'atelierVoletReplie';

/** État replié mémorisé (même pattern que la mémoire client / patrimoine : stockage injecté, jamais d'exception). Absent : déplié. */
export function lireVoletReplie(stockage) {
    try {
        return stockage.getItem(CLE_VOLET_REPLIE) === '1';
    } catch {
        return false;
    }
}

export function ecrireVoletReplie(stockage, replie) {
    try {
        if (replie) stockage.setItem(CLE_VOLET_REPLIE, '1');
        else stockage.removeItem(CLE_VOLET_REPLIE);
    } catch {
        // stockage bloqué : l'état ne survit pas au rechargement, l'atelier reste utilisable
    }
}

/* ------------------------------------------------------------------ L9-2 : onglets */

export const ONGLETS = [
    {id: 'carte', libelle: 'Carte'},
    {id: 'journees', libelle: 'Journées'},
    {id: 'sensibilite', libelle: 'Sensibilité'},
];
export const ONGLET_DEFAUT = 'carte';

/* ------------------------------------------------------------------ L9-3 : clic sur une journée */

/**
 * Calques après un clic sur la journée `cle` (`date|équipe`) : un seul calque de tournée visible, celui de cette journée.
 * Patrimoine et collecte gardent le choix en cours.
 */
export function calquesDeLaJournee(calquesTournees, cle, choix = {}) {
    const suite = {...choix};
    for (const c of calquesTournees || []) suite[`tournee:${c.cle}`] = c.cle === cle;
    return suite;
}

/** Positions à cadrer pour une journée : ses visites, plus départ et retour. Vide si la journée n'existe pas. */
export function positionsDeLaJournee(calquesTournees, cle) {
    const c = (calquesTournees || []).find((x) => x.cle === cle);
    if (!c) return [];
    return [c.depart, ...c.points.map((p) => p.position), c.retour].filter(Boolean);
}

/** Clic sur une journée dans l'onglet Journées : retour à la Carte, cette journée active et seule affichée. */
export function choisirJournee(etat, calquesTournees, cle, choix) {
    return {...etat, onglet: 'carte', journeeActive: cle, choixCalques: calquesDeLaJournee(calquesTournees, cle, choix)};
}

/** « Tout afficher » / « Tout masquer » : fixe d'un coup la visibilité des calques d'un groupe (`cles` = clés de calque), les autres choix sont gardés. */
export function reglerGroupe(choix, cles, visible) {
    const suite = {...choix};
    for (const c of cles) suite[c] = Boolean(visible);
    return suite;
}

/** Clé de journée (`date|équipe`) d'une ligne du détail. */
export const cleJournee = (j) => `${j.date}|${j.equipe}`;

/* ------------------------------------------------------------------ L9-4 / L9-5 : effets sur l'état de l'atelier */

/** « Appliquer ce cran » : le volet se rouvre pour que la valeur modifiée se voie ; l'onglet ne change pas. Le cran n'est pas joué. */
export const apresCran = (etat) => ({...etat, voletReplie: false});

/** « Jouer » (volet ouvert ou replié) : la carte porte le résultat, on revient sur l'onglet Carte. */
export const apresJouer = (etat) => ({...etat, onglet: 'carte'});

/* ------------------------------------------------------------------ L9-6 : états vides nommés */

/**
 * Message d'un onglet qui n'a rien à montrer, ou null s'il a du contenu. La Carte n'est jamais vide (patrimoine et
 * collecte se voient sans scénario joué) ; Journées et Sensibilité exigent un scénario terminé.
 * `attendu` : un scénario est à afficher (`?from=`) mais n'est pas encore arrivé ; `erreur` : sa lecture a échoué.
 */
export function etatVideOnglet(onglet, simulation, {attendu = false, erreur = false} = {}) {
    if (onglet === 'carte') return null;
    const statut = simulation?.statut;
    if (statut === 'done') return null;
    const sujet = onglet === 'journees' ? 'le détail jour par jour' : "l'analyse de sensibilité";
    if (!simulation && attendu) return erreur ? 'Impossible de charger le scénario.' : 'Chargement du scénario…';
    if (!simulation) return `Jouez un scénario pour voir ${sujet}.`;
    if (statut === 'failed') return `Le calcul a échoué : pas de ${onglet === 'journees' ? 'journées' : 'sensibilité'} à afficher.`;
    return `Calcul en cours : ${sujet} arrive à la fin du calcul.`;
}
